import { isNone } from '@ember/utils';

import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { BlockFontSize, BlockType, BlockVisualization } from 'webapp/enums/enums';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type BlockRecord from 'webapp/models/block';

const normalizeSettingsMap = {
	// in api: in model
	content: 'text',
	omitTimestamp: 'omitTimestamp',
	imageUrl: 'imageUrl',
	imageOriginalUrl: 'imageOriginalUrl',
	imageCrop: 'imageCrop',
	externalUrl: 'externalUrl',
	fontSize: 'fontSize',
	sortOrder: 'sortOrder',
	itemsInLine: 'itemsInLine',
	firstSliceConversion: 'firstSliceConversion',
	hideStepConversion: 'hideStepConversion',
	hideOverallConversionRate: 'hideOverallConversionRate',
	onlyMainMetricDimensions: 'onlyMainMetricDimensions',
	showOther: 'showOther',
	mediaEnabled: 'mediaEnabled',
	yAxisScale: 'yAxisScale',
	isSpline: 'isSpline',
	showTotals: 'showTotals',
	visualization: 'visualization',
	sortField: 'sortField',
	recordLimit: 'recordLimit',
	legendPosition: 'legendPosition',
	hasRowNumbers: 'hasRowNumbers',
	tableColumnType: 'tableColumnType',
	tableColumnInvert: 'tableColumnInvert',
	labelHeader: 'labelHeader',
	icon: 'icon',
	colors: 'colors',
	dsMap: 'dsMap',
	showTrafficLights: 'showTrafficLights',
	hideTimeLeft: 'hideTimeLeft',
} as const satisfies Partial<
	Record<keyof RecordData.Block.Settings, RecordData.Block.SettingsKey | 'text'>
>;

type SettingsApiKey = keyof typeof normalizeSettingsMap;

export default class BlockSerializer extends ApplicationSerializer {
	override getReadOnlyFields(
		_json: Record<string, unknown>,
	): (RecordData.Block.ReadOnlyKey | 'compares')[] {
		return [
			'localId',
			'dateRange', // the change will be picked up by board state (previews)
			'compares', // not a record attribute, BufferedModel.toJSON() adds it from the custom model
			'isGenerating',
		];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Block.Raw | RecordData.Block.Serialized,
		prop: string | undefined,
	) {
		const { settings, fromMetric, ...data } = json;
		const attributes: Partial<RecordAttributes<BlockRecord>> & { id?: string | number } = {
			...data,
		};

		if (settings) {
			(Object.keys(normalizeSettingsMap) as SettingsApiKey[]).forEach((inApiKey) => {
				if (inApiKey in settings) {
					(attributes as Record<string, unknown>)[normalizeSettingsMap[inApiKey]] =
						settings[inApiKey];
				}
			});

			const aiSettings = settings.aiSummary;
			if (settings.visualization === BlockVisualization.AI_SUMMARY && aiSettings) {
				attributes.aiSummaryShowTrend = !!aiSettings.showTrend;
				attributes.aiSummaryLanguage = aiSettings.language;
				attributes.aiSummaryContextLevelType = aiSettings.contextLevelType;
				attributes.aiSummaryLengthType = aiSettings.lengthType;
				attributes.aiSummaryStatus = aiSettings.status;

				if (aiSettings.editInfo) {
					attributes.aiSummaryEditedBy = aiSettings.editInfo.editedBy;
					attributes.aiSummaryEditedOn = aiSettings.editInfo.editedOn;
				}
			}

			const position = settings.position;
			if (position) {
				// legacy positions (without m) are on a grid half the size
				const scale = position.m ? 1 : 2;
				attributes.posX = position.x * scale;
				attributes.posY = position.y * scale;
				attributes.width = position.w * scale;
				attributes.height = position.h * scale;
				attributes.posMigrated = true;
			}

			// default fontsize
			if (!attributes.fontSize) {
				attributes.fontSize = BlockFontSize.MEDIUM;
			}
		}

		if (
			fromMetric &&
			(Object.keys(fromMetric.metricSetup || {}).length || fromMetric.metricKey)
		) {
			// null becomes {} through the object transform
			attributes.fromMetricSetup = this.normalizeMetricSetupData(fromMetric.metricSetup)!;
			if (fromMetric.dateRange) {
				attributes.fromMetricDateRange = fromMetric.dateRange;
			}
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Block.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<BlockRecord>,
			RecordData.Block.ReadOnlyKey
		>;

		const settings: RecordData.Block.Settings = {};
		(Object.keys(normalizeSettingsMap) as SettingsApiKey[]).forEach((inApiKey) => {
			const value = attributes[normalizeSettingsMap[inApiKey]];
			if (!isNone(value)) {
				(settings as Record<string, unknown>)[inApiKey] = value;
			}
		});

		if (settings.visualization !== BlockVisualization.IMAGE) {
			delete settings.imageUrl;
			delete settings.imageOriginalUrl;
			delete settings.imageCrop;
			delete settings.externalUrl;
		}

		if (settings.visualization !== BlockVisualization.TEXT) {
			delete settings.omitTimestamp;
		}

		if (settings.visualization !== BlockVisualization.TABLE) {
			delete settings.tableColumnType;
			delete settings.tableColumnInvert;
			delete settings.labelHeader;
		}

		if (settings.dsMap && !Object.keys(settings.dsMap).length) {
			delete settings.dsMap;
		}

		if (settings.visualization === BlockVisualization.AI_SUMMARY) {
			const aiSummary: RecordData.Block.AiSummary = {
				showTrend: attributes.aiSummaryShowTrend,
				language: attributes.aiSummaryLanguage,
				contextLevelType: attributes.aiSummaryContextLevelType,
				lengthType: attributes.aiSummaryLengthType,
				status: attributes.aiSummaryStatus,
			};
			// only include editInfo if both editedBy and editedOn are set
			// otherwise BE sets the value if empty object is sent
			if (attributes.aiSummaryEditedBy && attributes.aiSummaryEditedOn) {
				aiSummary.editInfo = {
					editedBy: attributes.aiSummaryEditedBy,
					editedOn: attributes.aiSummaryEditedOn,
				};
			}
			settings.aiSummary = aiSummary;
		}

		if (attributes.width) {
			settings.position = {
				x: attributes.posX,
				y: attributes.posY,
				w: attributes.width,
				h: attributes.height,
				m: true,
			};
		}

		const json: RecordData.Block.Serialized = {
			...omit(
				attributes,
				// moved to settings
				...Object.values(normalizeSettingsMap),
				// moved to settings.aiSummary
				'aiSummaryShowTrend',
				'aiSummaryLanguage',
				'aiSummaryContextLevelType',
				'aiSummaryLengthType',
				'aiSummaryEditedBy',
				'aiSummaryEditedOn',
				'aiSummaryStatus',
				// moved to settings.position
				'posX',
				'posY',
				'width',
				'height',
				'posMigrated',
				// moved to fromMetric
				'fromMetricSetup',
				'fromMetricDateRange',
			),
			settings,
			type: BlockType.UNI, // do we still need this?
		};

		const { fromMetricSetup, fromMetricDateRange } = attributes;
		if (fromMetricSetup && Object.keys(fromMetricSetup).length) {
			json.fromMetric = {
				metricSetup: this.serializeMetricSetupData(fromMetricSetup),
			};
			if (Object.keys(fromMetricDateRange || {}).length) {
				json.fromMetric.dateRange = fromMetricDateRange;
			}
		}

		return json;
	}
}
